import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { searchAll } from '../../apiClient';

// Debounce helper (preserved from the original Home page).
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const getEventStatus = (startDateStr) => {
  const now = new Date();
  const startDate = new Date(startDateStr);
  const diffHours = (startDate - now) / (1000 * 60 * 60);
  if (diffHours < 0 && diffHours > -3) return { label: 'Happening Now', className: 'bg-emerald-500 text-white' };
  if (diffHours > 0 && diffHours < 24) return { label: 'Today', className: 'bg-brand-500 text-white' };
  if (diffHours > 0 && diffHours < 48) return { label: 'Tomorrow', className: 'bg-purple-500 text-white' };
  return { label: 'Upcoming', className: 'bg-ink-400 text-white' };
};

// Self-contained search box used in the homepage hero.
// Behavior (searchAll + debounce + grouped results) is preserved from the
// original Home page; only the presentation is modernized.
const SearchBar = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState({ organizations: [], vendors: [], events: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const performSearch = async () => {
      if (debouncedSearchTerm.length < 2) {
        setSearchResults({ organizations: [], vendors: [], events: [] });
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const { data, error } = await searchAll(debouncedSearchTerm);
        if (error) throw error;
        setSearchResults({
          organizations: data.organizations || [],
          vendors: data.vendors || [],
          events: data.events || [],
        });
      } catch (err) {
        console.error('Search failed:', err);
        setError('Search failed. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    performSearch();
  }, [debouncedSearchTerm]);

  const groupedResults = [
    ...searchResults.organizations.map((org) => ({
      ...org,
      type: 'organization',
      logoURL: org.logourl || `https://ui-avatars.com/api/?name=${encodeURIComponent(org.company_name)}&background=1e3a8a&color=fff&size=128`,
    })),
    ...searchResults.vendors.map((vendor) => ({
      ...vendor,
      type: 'vendor',
      logoURL: vendor.logoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(vendor.company_name)}&background=2451e0&color=fff&size=128`,
    })),
  ].sort((a, b) => a.company_name.localeCompare(b.company_name));

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      <div className="relative">
        <span className="absolute left-5 top-1/2 -translate-y-1/2 text-ink-400 text-lg">🔎</span>
        <input
          type="text"
          placeholder="Search your school, district, event, or city…"
          className="w-full pl-12 pr-5 py-4 rounded-2xl text-base sm:text-lg text-ink-800 shadow-lift border-none outline-none focus:ring-4 focus:ring-accent-400/40 placeholder:text-ink-400"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {isLoading && (
          <div className="absolute right-5 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500" />
          </div>
        )}
      </div>

      {error && <div className="mt-2 text-accent-200 text-sm text-left">{error}</div>}

      {debouncedSearchTerm.length >= 2 && (
        <div className="absolute left-0 right-0 bg-white shadow-lift rounded-2xl mt-2 z-30 overflow-hidden text-left max-h-[400px] overflow-y-auto">
          {groupedResults.length > 0 && (
            <>
              <div className="px-4 py-2 bg-ink-50 text-ink-500 font-semibold text-sm">Schools / Organizations</div>
              {groupedResults.map((entity) => (
                <Link
                  to={entity.type === 'organization' ? `/organization-page/${entity.id}` : `/vendor-page/${entity.id}`}
                  key={`${entity.type}-${entity.id}`}
                  className="block hover:bg-brand-50 transition-colors"
                >
                  <div className="p-4 flex items-start">
                    <div className="flex-shrink-0 mr-3 w-12 h-12 rounded-lg overflow-hidden bg-ink-100 flex items-center justify-center">
                      <img
                        src={entity.logoURL}
                        alt={`${entity.company_name} logo`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(entity.company_name)}&background=1e3a8a&color=fff&size=128`;
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <h3 className="text-base font-bold text-ink-900 truncate mr-2">{entity.company_name}</h3>
                        <span className="inline-flex text-xs bg-ink-100 text-ink-600 px-2 py-0.5 rounded-full">
                          {entity.type === 'organization' ? 'District' : 'School'}
                        </span>
                      </div>
                      <p className="text-sm text-ink-500 truncate">{entity.address || 'Rio Grande Valley'}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </>
          )}

          {searchResults.events.length > 0 ? (
            <>
              <div className="px-4 py-2 bg-ink-50 text-ink-500 font-semibold text-sm">Events</div>
              {searchResults.events.map((event) => {
                const eventStatus = getEventStatus(event.start_date);
                const eventImage = event.default_image?.image_path ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(event.title || 'Event')}&background=2451e0&color=fff&size=128`;
                return (
                  <Link to={`/event-page/${event.id}`} key={event.id} className="block hover:bg-brand-50 transition-colors">
                    <div className="p-4 flex items-start">
                      <div className="flex-shrink-0 mr-3 w-12 h-12 rounded-lg overflow-hidden bg-ink-100">
                        <img
                          src={eventImage}
                          alt={event.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(event.title || 'Event')}&background=2451e0&color=fff&size=128`;
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2">
                          <div className="min-w-0">
                            <h3 className="text-base font-bold text-ink-900 truncate">{event.title}</h3>
                            <p className="text-sm text-ink-500 truncate">📍 {event.location}</p>
                          </div>
                          <span className={`flex-shrink-0 text-xs px-2 py-1 rounded-full ${eventStatus.className}`}>
                            {eventStatus.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </>
          ) : (
            groupedResults.length === 0 && !isLoading && (
              <div className="p-4 text-center text-ink-400">No results found</div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
